import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { AppSettings } from '@/types';
import { getSettings, saveSettings, migrateSettings, DEFAULT_SETTINGS, SETTINGS_VERSION } from '@/lib/data-layer';

interface Toast {
  id: string;
  type: 'success' | 'error' | 'info' | 'warning';
  title: string;
  description?: string;
  duration?: number;
}

interface UIState {
  // Settings
  settings: AppSettings;
  isSettingsLoaded: boolean;
  
  // Navigation
  isMobileNavOpen: boolean;
  isDesktopSidebarCollapsed: boolean;
  
  // Modals/Dialogs
  activeModal: string | null;
  modalData: Record<string, unknown> | null;
  
  // Toasts
  toasts: Toast[];
  
  // Keyboard shortcuts
  isShortcutsEnabled: boolean;
  
  // Loading states
  globalLoading: boolean;
  
  // Actions - Settings
  loadSettings: () => Promise<void>;
  updateSettings: (updates: Partial<AppSettings>) => Promise<void>;
  
  // Actions - Navigation
  setMobileNavOpen: (open: boolean) => void;
  toggleMobileNav: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleSidebar: () => void;
  
  // Actions - Modals
  openModal: (modalId: string, data?: Record<string, unknown>) => void;
  closeModal: () => void;
  
  // Actions - Toasts
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
  clearToasts: () => void;
  
  // Actions - Loading
  setGlobalLoading: (loading: boolean) => void;
  
  // Actions - Shortcuts
  setShortcutsEnabled: (enabled: boolean) => void;
}

let toastId = 0;

export const useUIStore = create<UIState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        settings: DEFAULT_SETTINGS,
        isSettingsLoaded: false,
        isMobileNavOpen: false,
        isDesktopSidebarCollapsed: false,
        activeModal: null,
        modalData: null,
        toasts: [],
        isShortcutsEnabled: true,
        globalLoading: false,
        
        // Load settings from DB
        loadSettings: async () => {
          try {
            const stored = await getSettings();
            let finalSettings = stored ?? DEFAULT_SETTINGS;

            // Apply one-time default flips to previously stored settings. Runs
            // here (not in the IndexedDB schema migration) because signed-in
            // users' settings live in Firestore.
            if (stored && (stored.settingsVersion ?? 1) < SETTINGS_VERSION) {
              finalSettings = migrateSettings(stored);
              await saveSettings(finalSettings);
            }

            set({
              settings: finalSettings,
              isSettingsLoaded: true,
            });
          } catch (error) {
            console.error('Failed to load settings:', error);
            set({ isSettingsLoaded: true });
          }
        },
        
        // Update settings
        updateSettings: async (updates) => {
          const newSettings = { ...get().settings, ...updates };
          
          try {
            await saveSettings(newSettings);
            set({ settings: newSettings });
          } catch (error) {
            console.error('Failed to save settings:', error);
          }
        },
        
        // Navigation
        setMobileNavOpen: (open) => {
          set({ isMobileNavOpen: open });
        },
        
        toggleMobileNav: () => {
          set((state) => ({ isMobileNavOpen: !state.isMobileNavOpen }));
        },
        
        setSidebarCollapsed: (collapsed) => {
          set({ isDesktopSidebarCollapsed: collapsed });
        },
        
        toggleSidebar: () => {
          set((state) => ({
            isDesktopSidebarCollapsed: !state.isDesktopSidebarCollapsed,
          }));
        },
        
        // Modals
        openModal: (modalId, data) => {
          set({ activeModal: modalId, modalData: data ?? null });
        },
        
        closeModal: () => {
          set({ activeModal: null, modalData: null });
        },
        
        // Toasts
        addToast: (toast) => {
          const id = `toast-${++toastId}`;
          const duration = toast.duration ?? 5000;
          
          set((state) => ({
            toasts: [...state.toasts, { ...toast, id }],
          }));
          
          // Auto remove after duration
          if (duration > 0) {
            setTimeout(() => {
              get().removeToast(id);
            }, duration);
          }
        },
        
        removeToast: (id) => {
          set((state) => ({
            toasts: state.toasts.filter((t) => t.id !== id),
          }));
        },
        
        clearToasts: () => {
          set({ toasts: [] });
        },
        
        // Loading
        setGlobalLoading: (loading) => {
          set({ globalLoading: loading });
        },
        
        // Shortcuts
        setShortcutsEnabled: (enabled) => {
          set({ isShortcutsEnabled: enabled });
        },
      }),
      {
        name: 'ui-preferences',
        partialize: (state) => ({
          isDesktopSidebarCollapsed: state.isDesktopSidebarCollapsed,
        }),
      }
    ),
    { name: 'ui-store' }
  )
);
